/**
 * One step on the linkage canvas: its name, its capability, its merge mode
 * when more than one link comes in, its step mode when it is not the default
 * merged one, and the problems a refused save named. `first` is drawn dark,
 * as the completed task the chain starts from, and takes no link in; on a
 * call it is drawn light, outlined in the call's color with the fork mark,
 * as the request its steps run beside (Feature 78 D8); on a schedule it keeps
 * its capability and carries a violet outline, a clock, and the cron's
 * reading (Feature 80 D18).
 *
 * Every node also carries a pair of hidden return handles at its foot, where
 * a write-back's arrow leaves its writer and reaches its target; they come
 * after the link handles, which an edge without a handle id attaches to.
 *
 * Resting the pointer on a node, or the keyboard on it, opens its card: In,
 * where each input field's value comes from, and Out, what later steps and
 * gates read of it.
 */
import { Icon } from '../../ui/finixt.js';
import { Handle, Position, type Node, type NodeProps } from '../../ui/flow.js';
import { HoverCard } from '../../ui/HoverCard.js';
import { NODE_WIDTH, type FlowNode } from './draft.js';
import { useNodeCard } from './hovered.js';
import type { CardLine, NodeCard } from './node-card.js';

export type StepNodeData = FlowNode['data'] & Record<string, unknown>;
export type StepNodeType = Node<StepNodeData, 'step'>;

const HANDLE = {
  width: 9,
  height: 9,
  background: 'var(--surface-card)',
  border: '1.5px solid var(--text-soft)',
};
const RETURN_HANDLE = {
  opacity: 0,
  width: 1,
  height: 1,
  minWidth: 0,
  minHeight: 0,
  border: 'none',
};
export const RETURN_OUT = 'return-out';
export const RETURN_IN = 'return-in';

const CHIP = {
  alignSelf: 'flex-start',
  marginTop: 2,
  padding: '2px 7px',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-meta-lg)',
  fontWeight: 'var(--weight-semibold)',
};

/** The card's outline: a problem first, then the selection, then the plain hairline. */
function outline(data: StepNodeData, selected: boolean): string {
  if (data.problems.length > 0) return '1.5px solid var(--danger)';
  if (selected) return '1.5px solid var(--blue)';
  if (data.schedule) return '1.5px solid var(--violet)';
  if (data.call) return '1.5px solid var(--info)';
  return data.first ? '1px solid var(--fill-action)' : '1px solid var(--border-input)';
}

/** The card's fill: dark for `first` on a completion or a schedule, light for a call's. */
function fill(data: StepNodeData): string {
  if (data.first && !data.call) return 'var(--fill-action)';
  return data.problems.length > 0 ? 'var(--danger-tint)' : 'var(--surface-card)';
}

const SECTION = {
  marginTop: 2,
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--text-label-sm)',
  letterSpacing: 'var(--tracking-label-tight)',
  textTransform: 'uppercase',
  color: 'var(--text-faint)',
} as const;

function Lines({ lines }: { lines: readonly CardLine[] }) {
  return lines.map((line) => (
    <span
      key={line.text}
      className="mono"
      style={{
        fontSize: 'var(--text-meta)',
        color: line.faint ? 'var(--text-faint)' : 'var(--text-strong)',
      }}
    >
      {line.text}
    </span>
  ));
}

/** A node's hover card: what it is, In, and Out. */
function NodeCardView({ card }: { card: NodeCard }) {
  return (
    <>
      <span className="mono" style={{ fontWeight: 'var(--weight-semibold)' }}>
        {card.title}
      </span>
      {card.inputs && (
        <>
          <span style={SECTION}>In · mapped</span>
          <Lines lines={card.inputs} />
        </>
      )}
      <span style={SECTION}>Out · read later</span>
      <Lines lines={card.outputs} />
    </>
  );
}

export function StepNode({ id, data, selected }: NodeProps<StepNodeType>) {
  const failing = data.problems.length > 0;
  const dark = data.first && !data.call;
  const muted = dark ? 'var(--text-ghost)' : 'var(--text-soft)';
  const card = useNodeCard(id);
  return (
    <HoverCard open={card !== null} content={card && <NodeCardView card={card} />}>
      <div
        style={{
          width: NODE_WIDTH,
          boxSizing: 'border-box',
          padding: '9px 11px',
          borderRadius: 'var(--radius-card)',
          background: fill(data),
          color: dark ? 'var(--text-inverse)' : 'var(--text-strong)',
          border: outline(data, selected),
          boxShadow: selected
            ? '0 0 0 3px var(--blue-tint)'
            : data.call
              ? '0 0 0 3px var(--info-tint)'
              : data.schedule
                ? '0 0 0 3px var(--violet-tint)'
                : 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {!data.first && <Handle type="target" position={Position.Left} style={HANDLE} />}
        <span
          className="mono"
          style={{ fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)' }}
        >
          {data.name}
        </span>
        <span className="mono" style={{ fontSize: 'var(--text-meta-lg)', color: muted }}>
          {data.capabilityId || 'no capability yet'}
        </span>
        {/* A schedule runs first's capability at each fire: its mark and reading (Feature 80 D18). */}
        {data.schedule && (
          <span
            aria-label="on a schedule"
            title={`${data.schedule.cron || 'no cron yet'} UTC`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--violet-tint)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            <Icon name="schedule" size={14} />
            {data.schedule.reading ??
              (data.schedule.cron ? `${data.schedule.cron} UTC` : 'no cron yet')}
          </span>
        )}
        {data.call && (
          <span
            aria-label="the call, forking"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--info)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            <Icon name="call_split" size={14} />
            the call
          </span>
        )}
        {data.first && !data.schedule && !data.call && (
          <span style={{ fontSize: 'var(--text-meta-lg)', color: muted }}>the completed task</span>
        )}
        {data.merge && (
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--blue)' }}>
            merge · {data.merge}
          </span>
        )}
        {failing && (
          <span
            style={{
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--danger)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            <span className="mono">{data.problems.length}</span>{' '}
            {data.problems.length === 1 ? 'problem' : 'problems'}
          </span>
        )}
        {data.mode === 'write_back' && (
          <span style={{ ...CHIP, background: 'var(--violet-tint)', color: 'var(--violet)' }}>
            write-back → <span className="mono">{data.target}</span>
          </span>
        )}
        {data.mode === 'independent' && (
          <span style={{ ...CHIP, background: 'var(--info-tint)', color: 'var(--info)' }}>
            independent
          </span>
        )}
        <Handle type="source" position={Position.Right} style={HANDLE} />
        <Handle
          id={RETURN_OUT}
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          style={RETURN_HANDLE}
        />
        <Handle
          id={RETURN_IN}
          type="target"
          position={Position.Bottom}
          isConnectable={false}
          style={RETURN_HANDLE}
        />
      </div>
    </HoverCard>
  );
}
